import React, { useEffect } from 'react';
import { useField, useForm } from 'react-final-form';
import { styled } from '@linaria/react';
import pluralize from 'pluralize';

import { useOrderStore, useRequestedWriterStore } from '@blocks/stores/order';

import { Label } from '../../ui';
import { Close, Star } from '../../ui/icon';

const WriterField = styled.div`
  display: flex;
  align-items: flex-start;
`;

const WriterClose = styled.button`
  background-color: transparent;
  border: none;
  border-radius: 50%;
  padding: 0;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3px;
  box-sizing: border-box;
  margin-left: 4px;

  &:hover {
    cursor: pointer;
    background-color: #f3f7fa;
  }
`;

const WriterContainer = styled.div`
  width: 100%;
  border-width: var(--bb-writer-border-width);
  border-style: var(--bb-writer-border-style);
  border-color: var(--bb-writer-border-color);
  border-radius: var(--bb-writer-border-radius);
  padding-top: var(--bb-writer-padding-top);
  padding-right: var(--bb-writer-padding-right);
  padding-bottom: var(--bb-writer-padding-bottom);
  padding-left: var(--bb-writer-padding-left);
  background-color: var(--bb-writer-background);
  box-sizing: border-box;
  display: flex;
  align-items: center;
`;

const WriterAvatar = styled.img`
  display: block;
  min-width: 40px;
  border-radius: 50%;
  margin-right: 8px;
`;

const WriterInfo = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  line-height: 22px;
  color: var(--bb-writer-color);
`;

const WriterColumn = styled.div``;

const WriterName = styled.div`
  color: var(--bb-writer-name-color);
  font-weight: bold;
  font-size: 12px;
  max-width: 88px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const WriterRating = styled.div`
  display: flex;
  align-items: center;
  font-weight: bold;
  font-size: 12px;
`;

const WriterStat = styled.div`
  font-size: 12px;
`;

const WriterSkeleton = styled.div`
  padding-top: var(--bb-writer-padding-top);
  padding-right: var(--bb-writer-padding-right);
  padding-bottom: var(--bb-writer-padding-bottom);
  padding-left: var(--bb-writer-padding-left);
  border-width: var(--bb-writer-border-width);
  border-style: solid;
  border-radius: var(--bb-writer-border-radius);
  border-color: #efefef;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    width: 120px;
    height: 100%;
    display: block;
    position: absolute;
    background: linear-gradient(90deg, transparent 0, #fff 50%, transparent);
    animation: skeleton-load 1s cubic-bezier(0.4, 0, 0.2, 1) infinite;
  }

  @-webkit-keyframes skeleton-load {
    0% {
      left: -150px;
    }

    to {
      left: 100%;
    }
  }

  @keyframes skeleton-load {
    0% {
      left: -150px;
    }

    to {
      left: 100%;
    }
  }
`;

const WriterSkeletonAvatar = styled.div`
  min-width: 40px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #efefef;
  margin-right: 8px;
`;

const WriterSkeletonInfo = styled.div`
  width: 100%;
`;

const WriterSkeletonLine = styled.div`
  min-width: 100%;
  width: 100%;
  height: 14px;
  margin: 5px 0;
  background-color: #efefef;
`;

const Writer = ({ domain }) => {
  const {
    input: { value: writer_req_slug },
  } = useField('writer_req_slug');
  const { setOrderAction } = useOrderStore();
  const { mutators } = useForm();

  const { writer, clearWriter, fetchWriter, isWriterLoading } = useRequestedWriterStore();

  const removeWriterOperations = () => {
    mutators.removeWriter();
    setOrderAction({ writer_req_slug: undefined });
    clearWriter();
  };

  useEffect(() => {
    fetchWriter({ domain, writer_req_slug });
  }, [domain, writer_req_slug, fetchWriter]);

  if (isWriterLoading) {
    return (
      <>
        <Label>Writer</Label>

        <WriterField>
          <WriterSkeleton>
            <WriterSkeletonAvatar />
            <WriterSkeletonInfo>
              <WriterSkeletonLine />
              <WriterSkeletonLine />
            </WriterSkeletonInfo>
          </WriterSkeleton>
          <WriterClose
            onClick={() => {}}
            type="button"
          >
            <Close />
          </WriterClose>
        </WriterField>
      </>
    );
  }
  if (writer && writer?.slug) {
    return (
      <>
        <Label>Writer</Label>
        <WriterField>
          <WriterContainer>
            <WriterAvatar
              src={writer.avatar_url}
              alt=""
              width="40"
              height="40"
            />
            <WriterInfo>
              <WriterColumn>
                <WriterName>{writer.pen_name}</WriterName>
                <WriterRating>
                  <Star />
                  {writer.rating}
                </WriterRating>
              </WriterColumn>

              <WriterColumn>
                <WriterStat>
                  <b>{writer.orders_finished_qty}</b>{' '}
                  {pluralize('paper', parseInt(writer.order_finished_qty, 10))}
                </WriterStat>
                <WriterStat>
                  <b>{writer.orders_progress_qty}</b> in progress
                </WriterStat>
              </WriterColumn>
            </WriterInfo>
          </WriterContainer>

          <WriterClose
            onClick={removeWriterOperations}
            type="button"
          >
            <Close />
          </WriterClose>
        </WriterField>
      </>
    );
  }
  return null;
};

export default Writer;
